import { monthShort, useLocale, useT } from "@/i18n";
import { useMemo, useState } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

interface MonthYearPickerProps {
  viewYear: number;
  viewMonth: number;
  currentYear: number;
  currentMonth: number;
  onSelect: (year: number, month: number) => void;
}

// Nothing tells us when the student first practiced, so this is a generous floor rather than an
// unbounded year list.
const OLDEST_SELECTABLE_YEARS_BACK = 5;

/** Jump straight to a month instead of clicking back through the calendar one month at a time. */
export function MonthYearPicker({
  viewYear,
  viewMonth,
  currentYear,
  currentMonth,
  onSelect,
}: MonthYearPickerProps) {
  const t = useT();
  const locale = useLocale();
  const [pickerYear, setPickerYear] = useState(viewYear);

  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, index) => monthShort(locale, index)),
    [locale]
  );

  const canGoPrevYear = pickerYear > currentYear - OLDEST_SELECTABLE_YEARS_BACK;
  const canGoNextYear = pickerYear < currentYear;

  return (
    <div className="absolute left-1/2 top-full z-10 mt-2 w-64 -translate-x-1/2 rounded-2xl bg-white p-3 shadow-app-card-hover">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setPickerYear((year) => year - 1)}
          disabled={!canGoPrevYear}
          aria-label={t["report.activity.previousYear"]}
          className="flex h-7 w-7 items-center justify-center rounded-full text-app-ink hover:bg-gray-100 disabled:opacity-40"
        >
          <HiChevronLeft className="h-4 w-4" />
        </button>
        <span className="app-value font-bold tabular-nums text-app-ink">{pickerYear}</span>
        <button
          type="button"
          onClick={() => setPickerYear((year) => year + 1)}
          disabled={!canGoNextYear}
          aria-label={t["report.activity.nextYear"]}
          className="flex h-7 w-7 items-center justify-center rounded-full text-app-ink hover:bg-gray-100 disabled:opacity-40"
        >
          <HiChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-4 gap-1">
        {monthLabels.map((label, index) => {
          const monthNumber = index + 1;
          const isFuture = pickerYear === currentYear && monthNumber > currentMonth;
          const isSelected = pickerYear === viewYear && monthNumber === viewMonth;
          return (
            <button
              type="button"
              key={label}
              disabled={isFuture}
              onClick={() => onSelect(pickerYear, monthNumber)}
              className={`rounded-lg py-1.5 text-sm font-bold disabled:opacity-30 ${
                isSelected ? "bg-app-ink text-white" : "text-app-text hover:bg-gray-100"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
